import { type ReactNode, useEffect, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Tooltip } from '../../../../components/Tooltip';
import { Modal } from '../../../../components/Modal';
import { Stepper } from '../../../../components/Stepper';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { LazyRichTextField } from '../../../../components/inputs/LazyRichTextField';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import {
  inventoryCategoriesApi,
  inventoryItemTypesApi,
  inventoryItemsApi,
  storageFacilitiesApi,
  unitsOfMeasureApi,
} from '../inventoryApi';
import type {
  InventoryCategoryOption,
  InventoryItemDetail,
  InventoryItemKind,
  InventoryItemTypeOption,
  LicensingModel,
  OwnershipType,
  UnitOfMeasureOption,
} from '../inventoryTypes';
import {
  ITEM_KIND,
  ITEM_KIND_DESCRIPTION,
  ITEM_KIND_LABEL,
  LICENSING_MODEL,
  LICENSING_MODEL_LABEL,

} from '../inventoryTypes';
import { sampleInventoryItem } from '../utils/sampleInventory';

function toNumberOrNull(raw: string): number | null {
  const trimmed = raw.trim();
  return trimmed === '' ? null : Number(trimmed);
}

/**
 * Which block of the form to draw.
 *
 * <b>This is what lets a profile card own a DEDICATED edit.</b> The rule is one card, one
 * modal over that card's fields and nothing else — a single form spanning every card puts
 * fields the reader did not come for in front of them and turns a one-field change into a
 * review of the whole record. Rather than three near-identical modal files, the form takes the
 * section as a prop and the profile's three cards each open it at their own.
 *
 * Undefined draws everything, which is the CREATE case — and there the sections become the
 * STEPS of a wizard rather than one long scroll. <b>Two steps, not three:</b> `identity` has
 * no step of its own and renders at the bottom of Details, so the step list and the card list
 * are deliberately not one to one. See `steps` for the argument.
 */
export type InventoryItemFormSection = 'details' | 'stock' | 'identity';

const SECTION_TITLE: Record<InventoryItemFormSection, string> = {
  details: 'Edit item details',
  stock: 'Edit stock control',
  identity: 'Edit product identity',
};

/**
 * A section the WIZARD draws as a step of its own.
 *
 * `identity` is deliberately not one — its fields render at the bottom of Details. Naming the
 * narrower set in the type is what keeps that structural: `FIELD_STEP` cannot name a section
 * with no step behind it, so a rejected field can always be navigated to.
 */
export type InventoryItemWizardStep = Exclude<InventoryItemFormSection, 'identity'>;

const STEP_LABEL: Record<InventoryItemWizardStep, string> = {
  details: 'Details',
  stock: 'Stock control',
};

/**
 * Which step holds which field, so a server rejection lands on a control that is on screen.
 *
 * Without it `focusValidationError` names the field, finds nothing mounted, and nothing moves
 * — which reads as the feature not being wired rather than as a rejection.
 */
const FIELD_STEP: Record<string, InventoryItemWizardStep> = {
  sku: 'details',
  inventoryCategoryId: 'details',
  inventoryItemTypeId: 'details',
  unitOfMeasureId: 'details',
  kind: 'details',
  description: 'details',
  reorderLevel: 'stock',
  maxStockLevel: 'stock',
  expiryThresholdDays: 'stock',
  estimatedUsefulLifeYears: 'stock',
  tracksExpiry: 'stock',
  tracksSerial: 'stock',

  /*
    The identity fields are on the DETAILS step, not a step of their own — see `steps` below.
    They keep `identity` as a SECTION, because the profile's Identity card opens the form at
    it; this map is read only while the form is stepped, so it names the step they render on.
  */
  manufacturer: 'details',
  brandName: 'details',
  model: 'details',
  licensingModel: 'details',
  ownership: 'details',
};

/**
 * Create or edit one catalog item.
 *
 * Three things here are rules rather than layout, and each is stated where it applies:
 * the category SUGGESTS the kind and never constrains it; the kind decides which
 * product-identity field is drawn, and the server both refuses a field the kind does not ask
 * for and clears one it no longer asks for; and expiry and useful life are mutually exclusive,
 * because an item spoils on a date or wears out over a span.
 */
export function InventoryItemModal({
  open,
  editing,
  section,
  initialItemType,
  initialCategory,
  initialUnitCode,
  initialKind,
  onClose,
  onSaved,
  onAdjust,
}: {
  open: boolean;
  /** Null creates. */
  editing: InventoryItemDetail | null;
  /** Undefined draws the whole form as a wizard — the create case. */
  section?: InventoryItemFormSection;
  /**
   * Seeds and LOCKS the item type on a create — for a form opened from a group that already
   * names one, which is Stock Levels' "Add item".
   *
   * The NAME travels with the id deliberately: `<Select>` resolves its label by finding the
   * value among its options, and the remote feed arrives a moment later — seeded with an id
   * alone the field shows its placeholder, and the form reads as though no type were set.
   * Injecting the name as an option is what states it from the first paint.
   */
  initialItemType?: { id: string; name: string };
  /**
   * Seeds and LOCKS the category on a create — the same shape, and the same reason, as
   * {@link initialItemType} beside it.
   *
   * Stock Levels' group kebab supplies all three together: a group IS an item type, and the
   * items already filed under it agree about which category and which kind they are. Seeding
   * only the type left the reader re-answering two questions the row they pressed had already
   * answered — and the category is the field the type picker is narrowed BY, so an unseeded one
   * left the locked type sitting above an empty category.
   *
   * The NAME travels with the id for the reason it does above: `<Select>` resolves its label
   * from its options, the remote feed arrives a moment later, and an id alone shows the
   * placeholder.
   */
  initialCategory?: { id: string; name: string };
  /**
   * SUGGESTS the unit on a create — the one the group's existing items are counted in.
   *
   * <b>A suggestion, not a lock, and that is the difference from the three props above it.</b>
   * The type, the category and the kind are FACTS about the group the reader opened this from,
   * so they are fixed. A unit is not: registering a case of something under a type counted in
   * pieces is a real thing to do, so the field offers an answer and gets out of the way — the
   * derivable-identifier rule the SKU suggester already follows.
   *
   * <b>It is a CODE, not an id</b>, because the level row carries no unit id. Resolving it
   * against the options this form already loads costs nothing and adds no wire field.
   */
  initialUnitCode?: string;
  /**
   * Seeds and LOCKS the kind on a create — for a form opened from a tab, or from a group whose
   * items already agree about it.
   *
   * The reference locks it for a stated reason: a registration started on Equipment must not
   * land under Consumables. The reader chose the kind by choosing which tab — or which group's
   * kebab — to press Create on, so the field has nothing left to ask.
   */
  initialKind?: InventoryItemKind;
  onClose: () => void;
  onSaved: () => void;
  /**
   * Hands the newly registered item straight to the opening-count form, when the reader asked
   * for it.
   *
   * **Its ABSENCE removes the checkbox** — the absent-callback idiom `<EditableAvatar>` already
   * uses, and it is what keeps the control from being dead: a caller that cannot open a count
   * form never offers one. The item profile's section edits supply nothing and draw nothing.
   */
  onAdjust?: (item: InventoryItemDetail) => void;
}) {
  const canCreateCategory = usePermission('inventory-categories.create');
  const canCreateItemType = usePermission('inventory-item-types.create');
  const canCreateUnit = usePermission('units-of-measure.create');

  /*
    The key of what the checkbox OPENS — and WHICH form that is depends on the KIND. A
    quantity item opens the lot count under `stock-batches.create`; a serialized one opens
    the stock-unit registration under `stock-units.create`.

    **Both are read unconditionally**, because a hook cannot be called on a branch —
    `canAdjust` picks between them below, after `tracksSerial` is derived.

    It was `stock-batches.create` alone until 22 Sep 2026, which was correct exactly while
    the tick was withheld from serialized items. The moment it was offered for one, that key
    was gating a control that opens a form it has nothing to do with — wrong in BOTH
    directions: a reader holding only `stock-units.create` saw no tick at all, and one
    holding only `stock-batches.create` was offered a registration the server then refused.

    Hidden without the relevant key, never disabled: this is a gate, not a state.
  */
  const canCountLots = usePermission('stock-batches.create');
  const canRegisterUnits = usePermission('stock-units.create');

  const [sku, setSku] = useState(editing?.sku ?? '');
  const [categoryId, setCategoryId] = useState(
    editing?.inventoryCategoryId ?? initialCategory?.id ?? '',
  );
  const [itemTypeId, setItemTypeId] = useState(
    editing?.inventoryItemTypeId ?? initialItemType?.id ?? '',
  );

  /*
    A type the reader added from the picker is STAGED, not written.

    It used to POST the moment they pressed Add, so abandoning the form left the catalog
    holding a row nobody chose — four of them arrived in under a minute from one form that
    never saved. The row is now created inside `save()`, immediately before the item, so a
    vocabulary only grows when the transaction that needed the word actually completes.

    The residual gap is stated rather than hidden: the two are separate calls under separate
    keys — the item create must NOT write the catalog, which is the "a key one request shape
    gets around" hole — so an item rejected by the server leaves the type behind. That window
    is one request wide instead of the whole time the form was open.
  */
  const PENDING_ITEM_TYPE = 'pending:item-type';
  const [pendingItemType, setPendingItemType] = useState<string | null>(null);

  // Locked only on a CREATE. An edit states what the record says it is, and correcting a
  // mis-filed item is exactly the thing that form is for.
  const lockedItemType = editing === null ? initialItemType : undefined;
  const lockedCategory = editing === null ? initialCategory : undefined;
  const [unitId, setUnitId] = useState(editing?.unitOfMeasureId ?? '');

  /*
    Whether the reader has ANSWERED the unit themselves. Starts true on an edit, because a
    stored value is data they chose and a form over existing data must not quietly replace it —
    the same guard `kindTouched` carries one field along.
  */
  const [unitTouched, setUnitTouched] = useState(editing !== null);
  const [description, setDescription] = useState(editing?.description ?? '');
  const [reorderLevel, setReorderLevel] = useState(String(editing?.reorderLevel ?? 0));
  const [maxStockLevel, setMaxStockLevel] = useState(
    editing?.maxStockLevel === null || editing?.maxStockLevel === undefined
      ? ''
      : String(editing.maxStockLevel),
  );
  const [manufacturer, setManufacturer] = useState(editing?.manufacturer ?? '');
  const [brandName, setBrandName] = useState(editing?.brandName ?? '');
  const [model, setModel] = useState(editing?.model ?? '');
  const [expiryThresholdDays, setExpiryThresholdDays] = useState(
    String(editing?.expiryThresholdDays ?? 180),
  );
  const [usefulLifeYears, setUsefulLifeYears] = useState(
    editing?.estimatedUsefulLifeYears === null || editing?.estimatedUsefulLifeYears === undefined
      ? ''
      : String(editing.estimatedUsefulLifeYears),
  );

  /*
    The kind is SUGGESTED by the category and settled by the reader.

    Derived during render rather than written by an effect, and it stops the moment the reader
    touches the field — the code suggester's rule exactly. `kindTouched` starts true on an edit,
    because a stored value is data the reader chose and a form over existing data must not
    quietly replace it.

    <b>The kind now sits ABOVE the category, so the suggestion runs upward.</b> A reader who
    answers the kind first settles it and the category never re-offers one; a reader who starts
    with the category watches the kind above them fill in. That is the cost of leading with the
    field that decides the shape of the form, and it is deliberate — the alternative is a form
    whose fields appear and vanish as the reader works down it.
  */
  const [kindChoice, setKindChoice] = useState(
    String(editing?.kind ?? initialKind ?? ITEM_KIND.consumable),
  );
  const [kindTouched, setKindTouched] = useState(editing !== null || initialKind !== undefined);

  // Locked only on a CREATE, like the item type. An edit exists to correct a mis-filed record.
  const lockedKind = editing === null ? initialKind : undefined;

  const [licensingModel, setLicensingModel] = useState(
    editing?.licensingModel === null || editing?.licensingModel === undefined
      ? ''
      : String(editing.licensingModel),
  );
  /*
    READ AND SENT, NEVER DRAWN — the form stopped asking for a tenure on 23 Sep 2026 and the
    stock registration owns it now.

    There is no setter, deliberately: nothing on this form may change it, and the value is kept
    only so an edit carries the stored declaration back untouched. Dropping it from the request
    would silently clear the tenure of every item already declaring one — a form that cannot
    SEE a value must not be able to destroy it.

    A new item leaves it empty, which every reader already treats as Owned.
  */
  const [ownership] = useState(
    editing?.ownership === null || editing?.ownership === undefined ? '' : String(editing.ownership),
  );

  const [tracksExpiryChoice, setTracksExpiryChoice] = useState(editing?.tracksExpiry ?? false);
  /*
    CHECKED BY DEFAULT on a CREATE, and still the reader's to turn off — product call,
    02 Oct 2026, in the reader's own words: *by default, make the identified by serial number as
    checked, but editable*.

    <b>An EDIT still reads the record</b>, which is what the `??` is for: a default is what a
    form opens with when it has nothing to go on, and a stored `false` is something to go on. A
    default applied over an existing row would silently re-point an item counted as a quantity
    at per-unit tracking the next time somebody corrected its name.

    <b>It is a DEFAULT, not a derivation.</b> The checkbox stays live for every kind but a
    subscription, which is forced on for its own reason — every licence expires on its own date
    — and says so in its own description.
  */
  const [tracksSerialChoice, setTracksSerialChoice] = useState(editing?.tracksSerial ?? true);

  /*
    WHAT HAPPENS AFTER SAVE, not a property of the record — so it is never sent, and an edit
    never offers it. Registering an item and counting its opening stock are two acts, and this
    is the reader saying they intend both.

    <b>DEFAULT ON from 22 Sep 2026.</b> An item registered and never counted holds nothing, so
    it reads as out of stock on every screen that reports one — which is the state almost nobody
    means to leave behind. Defaulting it off asked the reader to opt IN to the ordinary path.

    That is not the "a default that asserts something" failure: this is a workflow choice with
    no record behind it, and it commits nothing on its own. The second form still has to be
    filled in and saved, which is what makes a tick here safe to pre-answer.
  */
  const [adjustAfterSave, setAdjustAfterSave] = useState(true);

  /*
    Whether a storage facility EXISTS, which the count form needs and this form does not.

    Null means the question has not been answered yet — the fetch is in flight or was never
    made — and the checkbox stays enabled through it rather than flickering disabled on every
    open. A count form opened with no facility is a form that cannot be completed, which is
    the prerequisite banner's own argument one surface along.
  */
  const [hasFacility, setHasFacility] = useState<boolean | null>(null);

  const [categories, setCategories] = useState<InventoryCategoryOption[]>([]);
  const [itemTypes, setItemTypes] = useState<InventoryItemTypeOption[]>([]);
  const [units, setUnits] = useState<UnitOfMeasureOption[]>([]);
  /*
    TRUE on the first render, not false. The fetch is started by an effect, which runs AFTER
    paint — seeded false, the reader gets one frame of the real form over empty catalogs, then
    the skeleton, then the form again. The modal is rendered conditionally by every caller, so
    it unmounts on close and this is honest on every open.
  */
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);

  const [stepIndex, setStepIndex] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();
    setLoadingCatalogs(true);

    Promise.all([
      inventoryCategoriesApi.options(controller.signal),
      inventoryItemTypesApi.options(controller.signal),
      unitsOfMeasureApi.options(controller.signal),
    ])
      .then(([loadedCategories, loadedItemTypes, loadedUnits]) => {
        setCategories(loadedCategories);
        setItemTypes(loadedItemTypes);
        setUnits(loadedUnits);
      })
      // A failed picker load names no field, so it must not go through the save path's error
      // state — the fields render empty rather than the form reporting a rejection.
      .catch(() => undefined)
      .finally(() => setLoadingCatalogs(false));

    /*
      A SEPARATE request, and deliberately not folded into the three above.

      It answers a question about the FOLLOW-ON form rather than about this one, and only for a
      reader who can open one of them: either stock key, so a reader who will never see the
      checkbox never pays for the round trip. `storage-facilities.read` rides in on BOTH keys'
      own implied reads, which is what makes the call safe to fire at all.

      Either key rather than the resolved one, because the answer is the same question for both
      forms — is there anywhere to put stock — and `tracksSerial` is not derived yet here.

      A failure leaves the answer NULL and the checkbox enabled. The count form states the
      same prerequisite itself, so the worst case is the reader meeting it one screen later —
      which is better than a control disabled by a request that simply did not come back.
    */
    if (editing === null && (canCountLots || canRegisterUnits)) {
      storageFacilitiesApi
        .options(controller.signal)
        .then((facilities) => setHasFacility(facilities.length > 0))
        .catch(() => undefined);
    }

    return () => controller.abort();
  }, [open, editing, canCountLots, canRegisterUnits]);

  const suggestedKind = categories.find((c) => c.id === categoryId)?.defaultKind;

  const kind: InventoryItemKind =
    !kindTouched && suggestedKind !== null && suggestedKind !== undefined
      ? suggestedKind
      : (Number(kindChoice) as InventoryItemKind);

  /*
    WHICH CATEGORIES THIS KIND MAY BE FILED UNDER — product call, 06 Oct 2026: *make the kind
    have its own category*, raised against a screenshot of a form locked to Real Property still
    offering "Dental Supplies".

    **THIS REVERSES A WRITTEN DECISION, and that is why it is spelled out.**
    `InventoryCategory.DefaultKind` was documented as *"a suggestion, never a constraint"*, with
    a worked case — a consumable toner cartridge legitimately filed under ICT Equipment. The
    screenshot is the other side of that argument: the suggestion is harmless when the reader
    picks the category first and absurd when the kind is already locked.

    **A NULL `defaultKind` FITS EVERY KIND**, which is what keeps the reversal from hiding
    rows. Eleven of the twenty-six seeded categories carry none on purpose — Office Equipment,
    ICT Equipment, Furniture and Fixtures and the rest are kind-agnostic buckets — and they
    hold most of the items on the books. It is the rule `offeredItemTypes` below already
    applies to an unscoped item type, one field along.

    **THE CHOSEN ONE IS ALWAYS OFFERED, whatever it says.** An item already filed under a
    category this kind would now exclude must still show its own category when the reader opens
    it — otherwise the picker reads empty, the reader saves, and an edit of an unrelated field
    silently re-files the row. There is one such item on the books today.
  */
  const offeredCategories = categories.filter(
    (c) => c.defaultKind === null || c.defaultKind === undefined || c.defaultKind === kind || c.id === categoryId,
  );

  /*
    THE UNIT IS SUGGESTED, not demanded — the derivable-identifier rule the SKU suggester
    already follows, one field along.

    DERIVED DURING RENDER rather than written by an effect, so it follows the options the
    moment they land and cannot leave a stale answer on screen for a paint. It stops the
    instant the reader touches the field: `unitTouched` is a flag, never a comparison against
    the last suggestion, because a reader who deliberately picks the suggested unit has still
    answered it.

    Matched on the CODE, case-insensitively, because that is what the level row carries and
    what a reader recognises. An unmatched code simply suggests nothing.
  */
  const suggestedUnitId =
    initialUnitCode === undefined
      ? undefined
      : units.find((u) => u.code.toLowerCase() === initialUnitCode.toLowerCase())?.id;

  const resolvedUnitId = !unitTouched && suggestedUnitId !== undefined ? suggestedUnitId : unitId;

  /*
    Stepped on CREATE, one block on an EDIT — declared here rather than beside the step list,
    because the validity derivations below read it. A const used before its own declaration is a
    module-load failure rather than a compile one, which is the ordering trap the stock-levels
    slice list already documents.
  */
  const stepped = section === undefined;

  const isSubscription = kind === ITEM_KIND.subscription;
  const isAsset = kind === ITEM_KIND.asset;
  const isConsumable = kind === ITEM_KIND.consumable;

  /*
    REAL PROPERTY ASKS FOUR FEWER QUESTIONS — product call, 06 Oct 2026, in the reader's own
    words: *for real property, item type, sku, unit (default value piece), manufacturer, should
    all be hidden. use/rename Model as "Property Name"*.

    Each of the four is a question a building cannot answer. A property has no stock-keeping
    code, is counted in nothing but itself, has no item TYPE below its own kind, and is built
    rather than manufactured. All four are RESOLVED server-side rather than dropped: the SKU is
    assigned a `PROP-` code, the unit resolves the seeded `pc` row and the type the seeded
    `Real Property` row, each by name, exactly as a digital asset already resolves `lic`.

    The manufacturer is the one with no server answer, and it needs none — it is nullable on
    every kind, so hiding the control simply leaves it null.
  */
  const isRealProperty = kind === ITEM_KIND.realProperty;

  // A term licence expires at the end of its term, and a subscription seat is individually
  // identified. Both are forced rather than merely defaulted, and both say so on the control.
  const termLicence = isSubscription && licensingModel === String(LICENSING_MODEL.term);
  const tracksExpiry = termLicence ? true : tracksExpiryChoice;
  const tracksSerial = isSubscription ? true : tracksSerialChoice;

  /*
    WHETHER A DELIVERY APPLIES AT ALL, which is the row kebab's own rule read one surface back.

    A serialized item's stock is NAMED one row at a time, so there is no lot to receive — the
    server refuses one outright. Real property is registered rather than delivered, and the
    server refuses that too. Neither is withheld because of who is reading, so the control is
    ABSENT rather than disabled: an affordance that can never work is the dead control the
    hide rule forbids.

    **It was withheld from a SERIALIZED item until 22 Sep 2026, and that was the gap.** The
    reasoning was sound and its premise expired: serialized stock is NAMED one row at a time,
    so there was no count form to open — which left a reader registering a laptop with the tick
    absent and no way to record the twelve that arrived with it. There is one now, and it is a
    different form: `tracksSerial` decides which, and the caller reads the same flag off the
    record this handler is given.

    It still reads the DERIVED `tracksSerial`, not the checkbox behind it — a subscription is
    forced serialized whatever the box says, so reading the raw choice would open the wrong one
    of the two. Declared after that derivation for the same reason.

    REAL PROPERTY is still absent, and that one is permanent: it holds no stock at all, so
    neither form applies and the server refuses both.

    An EDIT never offers it either. Correcting a record is not registering one, and "after
    saving" would be a promise attached to the wrong act.
  */
  // Which key gates the tick is the KIND's question, and it is answered here rather than at
  // the hook, because `tracksSerial` is what decides which of the two forms opens.
  const canAdjust = tracksSerial ? canRegisterUnits : canCountLots;

  const adjustApplies =
    editing === null &&
    onAdjust !== undefined &&
    canAdjust &&
    kind !== ITEM_KIND.realProperty;

  /*
    The item-type picker is NARROWED by the category: that category's names plus every unscoped
    one. It is always DRAWN and disabled until a category is chosen, with the reason in its own
    description — hiding it would make a required field appear out of nowhere and push every
    field below it down the form as the reader works.
  */
  const offeredItemTypes = categoryId
    ? itemTypes.filter((t) => t.inventoryCategoryId === null || t.inventoryCategoryId === categoryId)
    : [];

  const reorderNumber = Number(reorderLevel);
  const maxNumber = maxStockLevel.trim() === '' ? null : Number(maxStockLevel);
  const stockLevelsOrdered = maxNumber === null || maxNumber >= reorderNumber;

  /*
    WHETHER THE STOCK BLOCK IS EVER PUT TO THIS READER.

    A digital asset's wizard has no Stock control step — product call, 23 Sep 2026 — so every
    value it would have collected is DERIVED instead: nothing to reorder, no ceiling, serialized
    by force, and expiring exactly when a term licence's term does. Asking for them would be a
    stage of work whose every answer this product already knows.

    An EDIT is unaffected, whatever the kind: `section` is set, the card the reader opened is
    what they see, and the state came off the record rather than from a default.
  */
  const stockAsked = !stepped || !isSubscription;

  const detailsValid =
    categoryId !== '' &&
    // Not asked for on REAL PROPERTY, where the server resolves the seeded type.
    (isRealProperty || itemTypeId !== '') &&
    // Neither is asked for on a digital asset or on real property — the server assigns the SKU
    // and resolves the unit — so gating the step on a field nobody can see would be a dead Next.
    (isSubscription || isRealProperty || (sku.trim().length > 0 && resolvedUnitId !== ''));

  const stockValid =
    reorderLevel.trim() !== '' &&
    Number.isFinite(reorderNumber) &&
    reorderNumber >= 0 &&
    stockLevelsOrdered;

  /*
    THE IDENTITY BLOCK'S OWN REQUIRED FIELDS, one per kind that has any.

    A subscription must say how it is licensed; an ASSET must carry a brand — product call,
    02 Oct 2026. Nothing is required of a consumable or of real property, so both are vacuously
    valid, which is what keeps this one expression rather than a branch per kind.
  */
  const identityValid =
    (!isSubscription || licensingModel !== '') && (!isAsset || brandName.trim() !== '');

  const valid = detailsValid && (stockAsked ? stockValid : true) && identityValid;

  // ── Details ─────────────────────────────────────────────────────────────────────────

  const detailsContent = (
    <div className="flex flex-col gap-4">
      {/*
        The KIND leads, full width, above everything. It is the field that decides the shape of
        the rest of the form — which identity field is drawn, whether stock is counted or named
        — so asking it first is asking the question the other answers depend on. Full width
        because its options carry a sublabel each, and at half a row those wrap into the control.
      */}
      <Select
        id="inventory-item-kind"
        label="Kind"
        required
        description={
          lockedKind === undefined
            ? 'How the item moves through the catalog. It decides which identity fields are asked for and whether stock is counted or named.'
            : 'Fixed by the tab this was opened from, so the item cannot land under another one.'
        }
        value={String(kind)}
        onChange={(next) => {
          setKindChoice(next);
          setKindTouched(true);
        }}
        sort="none"
        clearable={false}
        disabled={saving || lockedKind !== undefined}
        options={Object.entries(ITEM_KIND_LABEL).map(([value, label]) => ({
          value,
          label,
          sublabel: ITEM_KIND_DESCRIPTION[Number(value) as InventoryItemKind],
        }))}
        error={error?.fieldError('kind')}
      />

      <Select
        id="inventory-item-inventory-category-id"
        label="Category"
        required
        description={
          lockedCategory
            ? 'Fixed by the group this was opened from. Choose a different one on the Items page.'
            : 'What the item is. Only the categories this kind is filed under are offered, plus the ones that fit any kind.'
        }
        placeholder="Choose a category"
        value={categoryId}
        onChange={(next) => {
          setCategoryId(next);

          /*
            The member CLEARS when the container changes: an item type scoped to the
            previous category is not offered under this one, and leaving it would post a
            value the picker no longer shows.
          */
          const stillOffered = itemTypes.some(
            (t) =>
              t.id === itemTypeId && (t.inventoryCategoryId === null || t.inventoryCategoryId === next),
          );

          if (!stillOffered) setItemTypeId('');
        }}
        /*
          The locked category is INJECTED as an option, exactly as the locked type is one
          field along: `<Select>` resolves its label from its options and the remote feed
          arrives a moment later, so an id alone would leave the field showing its
          placeholder while claiming to be fixed.
        */
        options={
          lockedCategory
            ? [{ value: lockedCategory.id, label: lockedCategory.name }]
            : offeredCategories.map((c) => ({ value: c.id, label: c.name }))
        }
        remote
        loading={loadingCatalogs}
        disabled={saving || Boolean(lockedCategory)}
        // Gated on the CATALOG's own create key, never this form's update key — adding a
        // row is a write to another module. Hidden without it, and withheld outright while
        // the category is fixed: there is nothing to add to a field nobody can change.
        allowCustom={canCreateCategory && !lockedCategory}
        onCreateOption={
          canCreateCategory
            ? async (raw) => {
                /*
                  IT TAKES THE KIND THE FORM IS ON, where it used to be created unscoped.

                  With the picker now scoped, a category created with no kind would be offered
                  under every one of them — so a reader adding "Land and Buildings" from a
                  property form would find it on the Expendables form tomorrow. The kind is the
                  one fact this form knows about the row it is creating, and the catalog screen
                  in Settings is where it is widened or corrected.

                  The property form no longer DEPENDS on this — `Land`, `Buildings` and
                  `Office Spaces` are seeded, captured from the reference on 06 Oct 2026 — so
                  this is the widening the reader asked for rather than the only way in.
                */
                const created = await inventoryCategoriesApi.create({
                  name: raw.trim(),
                  code: null,
                  description: null,
                  defaultKind: kind,
                });

                // Appended rather than refetched: a refetch would drop whatever somebody
                // else filled in meanwhile and the reader would watch their selection go.
                setCategories((current) =>
                  current.some((c) => c.id === created.id)
                    ? current
                    : [...current, { id: created.id, name: created.name, defaultKind: created.defaultKind }],
                );

                setCategoryId(created.id);
                setItemTypeId('');
              }
            : undefined
        }
        error={error?.fieldError('inventoryCategoryId')}
      />

      {/*
        ABSENT on real property, where the server resolves the seeded `Real Property` row. It is
        the `isSubscription` treatment the SKU and the unit already take below: a control that
        can never be answered is noise, and one the form quietly ignores is worse.
      */}
      {isRealProperty ? null : (
      <Select
        id="inventory-item-inventory-item-type-id"
        label="Item type"
        required
        description={
          lockedItemType
            ? 'Fixed by the group this was opened from. Choose a different one on the Items page.'
            : categoryId
              ? 'The governed name this item is drawn from. Correcting it in the catalog corrects every item using it.'
              : 'Choose a category first — the names offered depend on it.'
        }
        placeholder={categoryId ? 'Choose an item type' : 'Choose a category first'}
        value={itemTypeId}
        onChange={(next) => {
          setItemTypeId(next);

          // Picking a real row drops the staged name, or the save would create a type
          // the reader has since changed their mind about.
          if (next !== PENDING_ITEM_TYPE) setPendingItemType(null);
        }}
        /*
          The locked type is INJECTED as an option so the field states its name from the
          first paint. `<Select>` resolves its label by finding the value among its options,
          and the remote feed arrives a moment later — seeded with an id alone the field
          shows its placeholder, and a form opened from a group reads as though no type
          were set.
        */
        options={
          lockedItemType
            ? [{ value: lockedItemType.id, label: lockedItemType.name }]
            : [
                // The staged name, so the field states what the reader typed rather than
                // falling back to its placeholder while the row does not exist yet.
                ...(pendingItemType
                  ? [{ value: PENDING_ITEM_TYPE, label: pendingItemType }]
                  : []),
                ...offeredItemTypes.map((t) => ({ value: t.id, label: t.name })),
              ]
        }
        remote
        loading={loadingCatalogs}
        // Disabled for STATE, not a gate: the reader may fill this in, they just have not
        // answered the question above it yet — or the group they opened this from already did.
        disabled={saving || Boolean(lockedItemType) || categoryId === ''}
        allowCustom={canCreateItemType && categoryId !== '' && !lockedItemType}
        onCreateOption={
          canCreateItemType
            ? async (raw) => {
                /*
                  STAGED, not written — see `pendingItemType`. Nothing reaches the catalog
                  until the item itself is saved, so a form the reader abandons leaves no
                  vocabulary behind.
                */
                setPendingItemType(raw.trim());
                setItemTypeId(PENDING_ITEM_TYPE);
              }
            : undefined
        }
        error={error?.fieldError('inventoryItemTypeId')}
      />
      )}

      {/*
        NEITHER IS ASKED FOR ON A DIGITAL ASSET — product call, 23 Sep 2026 — and both are still
        RECORDED, because the columns are load-bearing and neither is optional on the row.

        The SKU is assigned as `LIC-{yy}{NNN}`, the shape `FAC-26001` already takes: a licence
        product is not something anybody codes by hand, and a code a caller can name is a code a
        caller can misname into a permanent identifier. The UNIT resolves to the seeded `lic`
        row, because a licence is counted in licences and there is one honest answer.

        Both resolutions are the SERVER's. A form deriving either would be a second decision
        about one fact, and the one that goes stale — and the SKU in particular cannot be
        derived client-side at all without racing another reader for the number.
      */}
      {/*
        NEITHER IS DRAWN on a digital asset or on REAL PROPERTY. Both kinds have their code
        assigned and their unit resolved server-side — `LIC-`/`lic` and `PROP-`/`pc` — so there
        is nothing here for the reader to answer.
      */}
      {isSubscription || isRealProperty ? null : (
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="inventory-item-sku"
          label="SKU"
          required
          description="The stock-keeping code, e.g. ICT-LAPTOP-14. It is permanent: a deleted item keeps it forever, because a label, a requisition and a count sheet all quote it."
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          maxLength={60}
          disabled={saving}
          error={error?.fieldError('sku')}
        />

        <Select
          id="inventory-item-unit-of-measure-id"
          label="Unit"
          required
          description="What stock is counted in. Quantities are summed by it, so a second spelling of one unit is a stock figure nothing can add up."
          placeholder="Choose a unit"
          value={resolvedUnitId}
          onChange={(next) => {
            setUnitId(next);
            setUnitTouched(true);
          }}
          options={units.map((u) => ({ value: u.id, label: u.name, right: u.code, keywords: u.code }))}
          sort="none"
          remote
          loading={loadingCatalogs}
          disabled={saving}
          allowCustom={canCreateUnit}
          onCreateOption={
            canCreateUnit
              ? async (raw) => {
                  const typed = raw.trim();

                  /*
                    A unit needs a code AND a name, and the reader typed one thing. The typed
                    text becomes both — the code is what a row is written in, so it is the
                    half that matters — and Settings is where a fuller name is given.
                  */
                  const created = await unitsOfMeasureApi.create({
                    code: typed,
                    name: typed,
                    description: null,
                  });

                  setUnits((current) =>
                    current.some((u) => u.id === created.id)
                      ? current
                      : [...current, { id: created.id, code: created.code, name: created.name }],
                  );

                  setUnitId(created.id);
                  setUnitTouched(true);
                }
              : undefined
          }
          error={error?.fieldError('unitOfMeasureId')}
        />
      </div>
      )}

    </div>
  );

  /*
    The DESCRIPTION is its own node so the Details step can order it LAST — below the
    manufacturer and model rather than above them. Product call, 19 Sep 2026.

    It is the tallest control on the form by a long way, and a rich-text editor between two
    runs of one-line fields reads as the end of the form: the reader scrolls past it and does
    not find the identity row underneath. Prose belongs after the facts it describes.

    It is a DOCUMENT rather than a line: a specification carries a table of dimensions, a list
    of what is in the box, a paragraph of handling instructions. Two consequences travel with
    that and neither is cosmetic — the stored HTML is sanitized SERVER-SIDE on write (the
    editor's own parse protects the editor, not the database), and the item search no longer
    reads this field, because LIKE over markup matches every description CONTAINING a table
    rather than the ones that mention one.
  */
  const descriptionContent = (
    <LazyRichTextField
      id="inventory-item-description"
      label="Description"
      description="The item's specification. Optional, and formatted — headings, lists and a table are all available."
      value={description}
      onChange={setDescription}
      disabled={saving}
      error={error?.fieldError('description')}
    />
  );

  /*
    WHAT HAPPENS NEXT, not a property of the item — so it is separated by a rule rather than
    sitting in the run of record fields. The checkboxes above say what the item IS; this one
    says what the reader intends to do once it exists, and mixing the two kinds in one stack is
    what the separator refuses.

    <b>It renders on whichever step is LAST</b>, which for a digital asset is Details — product
    call, 23 Sep 2026, and it follows the step rather than being moved to a new home. That is
    what keeps it beside the confirm tick it precedes: a hand-off offered on step one of two
    would be a promise made before the reader had seen what they were registering.

    Absent entirely where a follow-on form cannot apply — see `adjustApplies`. Disabled, with
    the reason, where one could apply and there is nowhere to put the stock.
  */
  const adjustContent = adjustApplies ? (
    <div className="border-t border-slate-200 pt-4">
      <Tooltip
        block
        /*
          Named for the form that would OPEN, because the three are different acts and the reader
          is being told why one of them cannot run yet. One sentence covering all of them would
          describe none.
        */
        disabledReason={
          hasFacility === false
            ? isSubscription
              ? 'Add a storage facility first — every licence is held somewhere, and the form could not be completed without one.'
              : tracksSerial
                ? 'Add a storage facility first — every unit is kept somewhere, and the form could not be completed without one.'
                : 'Add a storage facility first — a count is taken somewhere, and the form could not be completed without one.'
            : null
        }
      >
        <label
          htmlFor="inventory-item-adjust-after-save"
          className="flex items-start gap-2 text-sm text-slate-600"
        >
          <Checkbox
            id="inventory-item-adjust-after-save"
            name="inventory-item-adjust-after-save"
            checked={adjustAfterSave && hasFacility !== false}
            onChange={() => setAdjustAfterSave((v) => !v)}
            disabled={saving || hasFacility === false}
          />
          <span>
            {/*
              THE READER'S OWN WORD for what they are about to record. A digital asset's stock is
              LICENCES — that is what the register calls them, what the form that opens is
              titled, and what the row it writes is — so "stock quantity" here would name the
              screen they meet by a word it does not use.
            */}
            {isSubscription ? 'Proceed adding licences.' : 'Proceed adding stock quantity.'}
            {/*
              The sentence under it names the form that is about to open, because the three are
              genuinely different acts: a quantity item is COUNTED into a lot, a serialized one
              has each unit NAMED, and a licence is registered one number at a time with its
              billing beside it. One wording covering them would describe none, and the reader
              would meet a form they were not told about.
            */}
            <span className="block text-xs text-slate-500">
              {isSubscription
                ? 'The item is registered first, then the licence registration opens against it, where each seat is listed by its own licence number. Nothing is recorded until that second form is saved on its own.'
                : tracksSerial
                  ? 'The item is registered first, then the stock unit form opens against it, where each unit is listed by its own serial number. Nothing is received until that second form is saved on its own.'
                  : 'The item is registered first, then the stock adjustment form opens against it. Nothing is counted until that second form is saved on its own.'}
            </span>
          </span>
        </label>
      </Tooltip>
    </div>
  ) : null;

  // ── Stock control ───────────────────────────────────────────────────────────────────

  const stockContent = (
    <div className="flex flex-col gap-4">
      {/*
        NO unit-cost field — the catalog row stopped carrying one on 20 Sep 2026. A price is a
        fact about the STOCK, so it is asked for when a lot is received or a unit is named, and
        Stock Levels' Investment column is what totals them.
      */}
      {/*
        ONE grid over the pair, not one each. They are the two ends of a single decision — the
        floor that reads as low stock and the ceiling that reads as overstock, checked against
        each other on save — so they belong on one row, and the reader compares them without
        moving down the form.

        Split into two containers each held ONE field in a half-width cell, which is the
        stranded-field failure exactly: a lone field in a grid of its own reads as a section
        with one item. They were a run of three while the unit cost led it, and removing that
        field is what left both of them alone.
      */}
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="inventory-item-reorder-level"
          label="Reorder level"
          required
          type="number"
          min={0}
          description="At or below this, the item reads as low stock. Nothing is blocked and nothing is ordered automatically."
          value={reorderLevel}
          onChange={(e) => setReorderLevel(e.target.value)}
          disabled={saving}
          error={error?.fieldError('reorderLevel')}
        />

        <TextField
          id="inventory-item-max-stock-level"
          label="Maximum stock level"
          type="number"
          min={0}
          description="Optional ceiling, used to size an order and flag overstock."
          value={maxStockLevel}
          onChange={(e) => setMaxStockLevel(e.target.value)}
          disabled={saving}
          error={
            stockLevelsOrdered
              ? error?.fieldError('maxStockLevel')
              : 'The maximum stock level cannot be below the reorder level.'
          }
        />
      </div>

      <label
        htmlFor="inventory-item-tracks-serial"
        className="flex items-start gap-2 text-sm text-slate-600"
      >
        <Checkbox
          id="inventory-item-tracks-serial"
          name="inventory-item-tracks-serial"
          checked={tracksSerial}
          onChange={() => setTracksSerialChoice((v) => !v)}
          disabled={saving || isSubscription}
        />
        <span>
          Each unit is identified by its own serial number.
          <span className="block text-xs text-slate-500">
            {isSubscription
              ? 'A subscription is tracked per seat, because every licence expires on its own date — so this cannot be turned off.'
              : 'On for equipment tracked one machine at a time. Off for anything counted as a quantity.'}
          </span>
        </span>
      </label>

      <label
        htmlFor="inventory-item-tracks-expiry"
        className="flex items-start gap-2 text-sm text-slate-600"
      >
        <Checkbox
          id="inventory-item-tracks-expiry"
          name="inventory-item-tracks-expiry"
          checked={tracksExpiry}
          onChange={() => setTracksExpiryChoice((v) => !v)}
          disabled={saving || termLicence}
        />
        <span>
          Stock of this item expires.
          <span className="block text-xs text-slate-500">
            {termLicence
              ? 'A term licence expires at the end of its term, so this cannot be turned off.'
              : 'An item either expires on a date or wears out over a span — never both.'}
          </span>
        </span>
      </label>

      {/*
        ONE COLUMN, not the full width it had. Both branches hold a small integer — a day count
        and a year count — and a number stretched edge to edge is a box mostly full of nothing,
        which is the argument the date field already wins on its own width.

        The grid wraps whichever branch renders, so the two cannot end up different widths: a
        field that changed size as a checkbox moved would read as the form breaking rather than
        as a different question being asked.
      */}
      <div className="grid gap-4 sm:grid-cols-2">
      {tracksExpiry ? (
        <TextField
          id="inventory-item-expiry-threshold-days"
          label="Warn this many days before expiry"
          type="number"
          min={1}
          max={3650}
          description="This item's own warning window. A vaccine warns at 180 days and a reagent at 30, which is why it sits on the item rather than only on a facility-wide setting."
          value={expiryThresholdDays}
          onChange={(e) => setExpiryThresholdDays(e.target.value)}
          disabled={saving}
          error={error?.fieldError('expiryThresholdDays')}
        />
      ) : (
        <TextField
          id="inventory-item-estimated-useful-life-years"
          label="Estimated useful life (years)"
          type="number"
          min={1}
          max={200}
          description="Optional, for a durable that wears out rather than spoiling."
          value={usefulLifeYears}
          onChange={(e) => setUsefulLifeYears(e.target.value)}
          disabled={saving}
          error={error?.fieldError('estimatedUsefulLifeYears')}
        />
      )}
      </div>

      {/*
        WHAT HAPPENS NEXT, not a property of the item — so it is separated by a rule rather than
        sitting in the run of record fields above it. The two checkboxes higher up say what the
        item IS; this one says what the reader intends to do once it exists, and mixing the two
        kinds in one stack is what the separator refuses.

        Absent entirely where a count cannot apply — see `adjustApplies`. Disabled, with the
        reason, where one could apply and there is nowhere to put the stock.
      */}
      {adjustContent}
    </div>
  );


  // ── Product identity ────────────────────────────────────────────────────────────────

  const identityContent = (
    <div className="flex flex-col gap-4">
      {/*
        ABSENT on REAL PROPERTY — product call, 06 Oct 2026. A building is built rather than
        manufactured, and the field was the last one on this form asked of every kind.

        The description said so in as many words, and that sentence had to go with the control:
        a form that asks on three kinds and claims to ask on four is the stale prose the
        document rule is about.
      */}
      {isRealProperty ? null : (
        <TextField
          id="inventory-item-manufacturer"
          label="Manufacturer/Service Provider"
          description="Who makes it — or who provides it, where the item is a subscription or a service rather than a thing."
          value={manufacturer}
          onChange={(e) => setManufacturer(e.target.value)}
          maxLength={150}
          disabled={saving}
          error={error?.fieldError('manufacturer')}
        />
      )}

      {/*
        WHICH of these the kind draws, and an ASSET draws BOTH — product call, 02 Oct 2026, in
        the reader's own words: *for Equipment, add brand before the model. required*.

        A control that can never apply is noise, and one the form quietly ignores is worse — so
        a subscription and real property still see neither brand nor a second identity field
        beyond their own.

          consumable    Brand
          asset         Brand (REQUIRED) then Model
          subscription  Service/Model
          real property neither

        BRAND BEFORE MODEL, which is the reader's own order and the way the two are written: a
        Lenovo ThinkPad T14 is a brand and then a model, never the other way round.
      */}
      {isConsumable || isAsset ? (
        <TextField
          id="inventory-item-brand-name"
          label="Brand"
          /*
            REQUIRED on an asset and optional on a consumable, which is the one asymmetry here
            and is worth saying: a generic expendable is a real filing, where a piece of
            equipment with no brand cannot be told from the next model of the same type.
          */
          required={isAsset}
          description={
            isAsset
              ? 'The make this equipment is sold under — Lenovo against a Laptop item type. Required, because the brand and the model together are what tell two of them apart.'
              : 'The brand an expendable is bought under — Biogesic against a Paracetamol item type.'
          }
          value={brandName}
          onChange={(e) => setBrandName(e.target.value)}
          maxLength={150}
          disabled={saving}
          error={error?.fieldError('brandName')}
        />
      ) : null}

      {!isConsumable && !isRealProperty ? (
        <TextField
          id="inventory-item-model"
          /*
            `Service/Model` on a DIGITAL ASSET — product call, 23 Sep 2026, and it is the
            contained label/entity split `Manufacturer/Service Provider` already takes one
            field along. A subscription is a SERVICE as often as it is a versioned product, so
            a label reading only "Model" asks a question half of them cannot answer.

            The column, the DTO field, the request field, the input id and every rejection
            naming `model` are untouched: what changed is the word on screen, which is the
            reader's rather than the schema's.
          */
          /*
            REAL PROPERTY DRAWS NO IDENTITY FIELD AT ALL — product call, 06 Oct 2026, in the
            reader's own words: *hide the property name*. It was labelled `Property Name` for
            one day, which is the whole life of the paragraph this replaces.

            That leaves a property form with NOTHING the reader fills in to tell two items
            apart — no item type, no SKU, no unit, no manufacturer, no brand and now no model —
            so the CATEGORY becomes the distinguishing value and `DuplicateIdentityAsync`
            compares on it for this kind alone. **The consequence is stated rather than
            discovered: there is one real-property item per category**, and the buildings and
            spaces themselves are `RealProperties` rows registered under it.

            The COLUMN, the DTO field and the request field are untouched. The server CLEARS
            `Model` on this kind and the validator REFUSES one sent, which is the pair every
            kind-gated field here takes.
          */
          label={isSubscription ? 'Service/Model' : 'Model'}
          description={
            isSubscription
              ? 'The service, or the edition or version of the licence.'
              : 'The equipment model designation, e.g. ThinkPad T14.'
          }
          value={model}
          onChange={(e) => setModel(e.target.value)}
          maxLength={150}
          disabled={saving}
          error={error?.fieldError('model')}
        />
      ) : null}

      {isSubscription ? (
        <Select
          id="inventory-item-licensing-model"
          label="Licensing"
          required
          description="A term licence expires at the end of its term, so it always tracks expiry. A perpetual one never lapses."
          placeholder="Choose how it is licensed"
          value={licensingModel}
          onChange={setLicensingModel}
          sort="none"
          clearable={false}
          disabled={saving}
          options={Object.entries(LICENSING_MODEL_LABEL).map(([value, label]) => ({ value, label }))}
          error={error?.fieldError('licensingModel')}
        />
      ) : null}

      {/*
        THE ITEM NO LONGER DECLARES ITS TENURE — product call, 23 Sep 2026, and the field is
        gone rather than relabelled.

        A catalog row said how a designation is "usually held" and every unit fell back to it.
        That reads as a sensible default and is the wrong owner: this document's own words are
        that tenure is captured per UNIT, because three rented vans can come from three
        companies on three contracts. A default on the type is a second place to answer a
        question the unit already answers, and the one nobody updates when a lease ends.

        **The stock registration owns it now**, where it is REQUIRED and defaults to Owned.

        The STATE survives and is still sent, deliberately: an edit that stopped carrying the
        value would silently clear the tenure of every item already declaring one, which is the
        government-ID lesson — a form that cannot SEE a value must not be able to destroy it.
        So a legacy declaration is preserved untouched, a new item leaves it null, and null
        already reads as Owned everywhere.
      */}
    </div>
  );

  /*
    Stepped on CREATE, one block on an EDIT.

    <b>TWO steps, and the identity fields sit at the bottom of Details.</b> Product call,
    18 Sep 2026 — it was three, one per profile card. Identity did not earn one: on a
    consumable that whole step is a manufacturer and a brand, so the rail promised a third
    stage of work that was two text boxes, and a reader filling in a Biogesic met a Next,
    a stepper head and a Back to type one word.

    <b>`identity` is still a SECTION</b>, and that is the half to keep: the profile's Identity
    card opens this form at it, which is what "one card, one modal" requires. What changed is
    where those fields RENDER while the form is stepped — the step list and the card list are
    no longer one to one, and the mirror rule survives it because nothing is asked for that
    the profile cannot show.

    An edit is never stepped: a card's dedicated edit is one section by definition, and a rail
    over a single step would be a progress indicator for a journey of one.
  */
  /*
    <b>ONE step for a digital asset</b>, because the Stock control step's every answer is
    derived for it — see `stockAsked`. A rail over a single step is a progress indicator for a
    journey of one, so the stepper is withheld below rather than drawn with one head on it.
  */
  const steps: InventoryItemWizardStep[] = stockAsked ? ['details', 'stock'] : ['details'];

  const CONTENT: Record<InventoryItemFormSection, ReactNode> = {
    // Appended rather than interleaved: what a thing IS comes before who made it, and the
    // create path reads top to bottom in the order the profile's cards are stacked.
    /*
      Facts, then who made it, then the document that describes it — see `descriptionContent`.
      An EDIT of this card keeps the same order for the same reason.
    */
    details: (
      <div className="flex flex-col gap-4">
        {detailsContent}
        {stepped ? identityContent : null}
        {descriptionContent}
        {/*
          The hand-off rides on whichever step is LAST, which is this one for a digital asset —
          its Stock control step is derived away, so a tick left there would be offered on a
          screen nobody sees. It is never drawn on an EDIT, because `adjustApplies` requires a
          create.
        */}
        {stepped && !stockAsked ? adjustContent : null}
      </div>
    ),
    stock: stockContent,
    identity: identityContent,
  };

  const STEP_VALID: Record<InventoryItemFormSection, boolean> = {
    // A subscription's licensing is required and now renders HERE, so the Next gate has to
    // read it — otherwise the step is passable while the field it drew is unanswered.
    details: detailsValid && (stepped ? identityValid : true),
    stock: stockValid,
    identity: identityValid,
  };

  /*
    CLAMPED, because the step LIST is derived from the kind and the kind is a field on step one.

    A reader standing on Stock control who changes the kind to a digital asset has the step they
    are standing on removed underneath them — and an index of 1 against a list of 1 resolves to
    `undefined`, which takes `CONTENT[undefined]` and the panel with it. Derived rather than
    reset in an effect, so it cannot be one render behind the list it is clamped to.
  */
  const safeStepIndex = Math.min(stepIndex, steps.length - 1);

  const currentStep = stepped ? steps[safeStepIndex] : section;
  const isLastStep = !stepped || safeStepIndex === steps.length - 1;

  const canSave = valid && confirmed && !saving;

  /*
    WHY a step cannot be left, and why Save is dead — one sentence naming the first thing
    missing.

    Both buttons were `disabled` with nothing to explain them, which is the rule this product
    states plainly: every disabled control says why. On a two-step form whose required fields
    sit below an open dropdown that reads as "it is not saving" — and it did, for a reader who
    created four item types trying to get past it.
  */
  /**
   * WHY the identity block is incomplete — one sentence, naming the field.
   *
   * It is a function rather than a constant because two kinds have a rule and they are
   * different rules: a subscription must say how it is licensed, an asset must carry a brand.
   * It read the subscription's sentence unconditionally, so an asset missing its brand was told
   * to choose a licensing model it has no field for.
   */
  function identityMissing(): string | null {
    if (isSubscription && licensingModel === '') return 'Choose how the subscription is licensed.';
    if (isAsset && brandName.trim() === '') return 'Enter the brand.';
    return null;
  }

  function missingOn(step: InventoryItemFormSection): string | null {
    if (step === 'details') {
      if (categoryId === '') return 'Choose a category.';
      if (!isRealProperty && itemTypeId === '') return 'Choose an item type.';

      // None of the three is drawn on a digital asset, and none on real property, so none can
      // be named as missing — a disabled control that names a field nobody can see is worse
      // than one with nothing to say.
      if (!isSubscription && !isRealProperty && sku.trim().length === 0) return 'Enter a SKU.';
      if (!isSubscription && !isRealProperty && resolvedUnitId === '') {
        return 'Choose a unit of measure.';
      }

      if (stepped) return identityMissing();
      return null;
    }

    if (step === 'stock') {
      if (reorderLevel.trim() === '' || !Number.isFinite(reorderNumber) || reorderNumber < 0) {
        return 'Enter a reorder level of zero or more.';
      }
      if (!stockLevelsOrdered) return 'The maximum must be at least the reorder level.';
      return null;
    }

    return identityMissing();
  }

  /*
    The SAVE reason walks every step, not just the one on screen — the reader is standing on
    the last step and the unfilled field is behind them, so naming the step is what makes the
    sentence actionable.
  */
  function saveBlockedReason(): string | null {
    if (saving) return null;

    for (const step of stepped ? steps : [section ?? 'details']) {
      const missing = missingOn(step);
      if (missing) return stepped ? `${STEP_LABEL[step as InventoryItemWizardStep]}: ${missing}` : missing;
    }

    return confirmed ? null : 'Tick the confirmation to save.';
  }


  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    /*
      The staged type is created FIRST and separately, under its own key. Folding it into
      the item create would be `inventory-items.create` silently writing a row that
      `inventory-item-types.create` governs — the hole the hospital's own item create has.
    */
    let resolvedItemTypeId = itemTypeId;

    if (pendingItemType !== null) {
      try {
        const created = await inventoryItemTypesApi.create({
          name: pendingItemType,
          description: null,

          // Scoped to the chosen category, because that is the question the reader just
          // answered. An unscoped name is a deliberate choice made in Settings.
          inventoryCategoryId: categoryId || null,
        });

        resolvedItemTypeId = created.id;
        setPendingItemType(null);
        setItemTypeId(created.id);

        setItemTypes((current) =>
          current.some((t) => t.id === created.id)
            ? current
            : [
                ...current,
                {
                  id: created.id,
                  name: created.name,
                  inventoryCategoryId: created.inventoryCategoryId,
                },
              ],
        );
      } catch (caught) {
        setError(
          caught instanceof ApiError
            ? caught
            : new ApiError('That item type could not be created.', 0),
        );
        setSaving(false);
        return;
      }
    }

    const request = {
      /*
        NULL on a digital asset CREATE, where the server assigns it — and the STORED value on an
        edit, whatever the kind. `sku` is seeded from the record, so an edit that cannot see the
        field still carries it back untouched: a form that cannot see a value must not be able
        to destroy it, and the server treats a blank on an update as "keep what is recorded".
      */
      sku: sku.trim() || null,

      /*
        NULL on a REAL-PROPERTY create, where the server resolves the seeded `Real Property`
        row — the `unitOfMeasureId` treatment below, one field along. An EDIT carries the
        stored id back untouched, because `itemTypeId` is seeded from the record: a form that
        cannot see a value must not be able to destroy it.
      */
      inventoryItemTypeId: resolvedItemTypeId || null,
      inventoryCategoryId: categoryId,
      kind,
      /*
        The RESOLVED unit, never the raw state: an untouched form posts what the field is
        showing, which is the suggestion. NULL on a digital asset, where the server resolves the
        seeded `lic` row — a licence is counted in licences.
      */
      unitOfMeasureId: resolvedUnitId || null,

      // An empty document is `<p></p>`, not an empty string — the field collapses it and the
      // server's sanitizer collapses it again, or a description nobody filled in would read as
      // present to every `is null` check downstream.
      description: description.trim() ? description : null,

      /*
        DERIVED WHERE THE STEP WAS NEVER DRAWN — see `stockAsked`. A digital asset has nothing to
        reorder and no ceiling to overstock, is serialized by force, and expires exactly when a
        term licence's term does. Posting the untouched defaults instead would work today and
        break the moment a reader switched kind mid-form with values already typed.
      */
      reorderLevel: stockAsked ? reorderNumber : 0,
      maxStockLevel: stockAsked ? toNumberOrNull(maxStockLevel) : null,
      tracksExpiry,
      expiryThresholdDays: tracksExpiry ? Number(expiryThresholdDays || 180) : 180,
      estimatedUsefulLifeYears:
        tracksExpiry || !stockAsked ? null : toNumberOrNull(usefulLifeYears),
      tracksSerial,
      // CLEARED on real property, whose form does not draw it. The state survives a kind
      // switch mid-form, so sending the raw value would file a manufacturer against a building
      // the reader last saw on a different kind.
      manufacturer: isRealProperty ? null : manufacturer.trim() || null,

      // Only what this kind asks for. Sending the rest would be refused, which is the server
      // saying the same thing this form already knows.
      // A CONSUMABLE and an ASSET both carry one; the other two kinds refuse it, and the
      // server clears it either way so no route can leave a stale value behind.
      brandName: isConsumable || isAsset ? brandName.trim() || null : null,
      // Real property joins the consumable here from 06 Oct 2026: its form draws no identity
      // field at all, so sending the state behind the hidden control would have an EDIT of an
      // existing property item refused on a field nobody can see.
      model: isConsumable || isRealProperty ? null : model.trim() || null,
      licensingModel: isSubscription ? (Number(licensingModel) as LicensingModel) : null,
      ownership: isAsset && ownership !== '' ? (Number(ownership) as OwnershipType) : null,
    };

    try {
      if (editing) {
        await inventoryItemsApi.update(editing.id, request);
        onSaved();
      } else {
        const created = await inventoryItemsApi.create(request);

        /*
          `onSaved` FIRST, then the hand-off. The parent closes this form and refreshes on the
          first call and opens the count form on the second, so the order is what decides
          which modal is on screen when React settles — and it keeps "one modal, never stacked"
          true: this one is already closing when the next is asked for.

          The tick is re-read against `hasFacility` rather than trusted on its own, so a reader
          who ticked it before the facilities answer arrived is not handed a form they cannot
          complete.
        */
        onSaved();

        if (adjustAfterSave && hasFacility !== false) onAdjust?.(created);
      }
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the item.', 0);

      setError(failure);
      focusValidationError(failure, {
        idPrefix: 'inventory-item',

        // A rejected field on another step is not in the DOM until that step renders, so the
        // form goes there first. Without this the reader is told which field is wrong and
        // nothing moves.
        stepOf: stepped
          ? (field) => {
              const owner = FIELD_STEP[field];
              if (!owner) return undefined;

              /*
                A step the WIZARD is not drawing resolves to nothing rather than to -1. A digital
                asset has no Stock control step, so a rejection naming one of its fields would
                otherwise switch to index -1 and blank the panel — the feature reading as broken
                on the one path where the server disagreed with the form.
              */
              const index = steps.indexOf(owner);
              return index >= 0 ? index : undefined;
            }
          : undefined,
        onStep: stepped ? setStepIndex : undefined,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? (section ? SECTION_TITLE[section] : 'Edit item') : 'Register item'}
      size="xl"
      id="inventory-item-modal"
      loading={loadingCatalogs}
      loadingFields={{ rows: 6 }}
      autogenerate={{
        onGenerate: () => {
          /*
            `offeredCategories`, NEVER the whole catalog. The picker is scoped to the kind from
            06 Oct 2026, so a generated id from outside that set resolves to no label: the field
            falls back to its placeholder and reads as unanswered while the request still
            carries the value. It is the same failure the locked pickers are guarded against
            just below, arriving through a different door.

            THE SET CAN STILL BE EMPTY — every category of a kind can be soft-deleted — and the
            generator then leaves the field unanswered rather than posting an id the picker does
            not hold. Save stays disabled until the reader adds one, which is honest: the
            alternative is Autogenerate CREATING a category, and a generator writing a row that
            `inventory-categories.create` governs is the key-bypass this form refuses one field
            along.
          */
          const sample = sampleInventoryItem(
            { categories: offeredCategories, itemTypes, units },
            { kind, tracksExpiry },
          );

          /*
            NEVER a field the reader cannot see. On a digital asset the SKU is the server's and
            the unit resolves, so generating either would put a value on the wire that the
            server ignores at best and refuses at worst — and the reader would have no control
            to correct it on.
          */
          if (!isSubscription && !isRealProperty) setSku(sample.sku);

          /*
            NEVER over a field the reader has already decided — the generator's own rule, and
            the three locked pickers are exactly that case: a form opened from a group's kebab
            is answering for THAT group.

            It is not cosmetic. A locked picker is handed ONE option (the injected name), so a
            generated id it does not hold resolves to no label at all: the field would fall
            back to its placeholder and read as unanswered while the request still carried the
            locked value. The kind is already safe, because the handler below re-states the
            derived `kind` rather than the sample's.
          */
          if (!lockedCategory && sample.inventoryCategoryId !== '') {
            setCategoryId(sample.inventoryCategoryId);
          }

          // The property form draws no item type — the server resolves one — so generating an
          // id would put a value on the wire with no control to correct it on.
          if (!lockedItemType && !isRealProperty) setItemTypeId(sample.inventoryItemTypeId);

          if (!isSubscription && !isRealProperty) {
            setUnitId(sample.unitOfMeasureId);

            // Setting a value is not the reader choosing one — but the generator HAS answered
            // the field, so the suggestion must stop or it would paint over the generated unit.
            setUnitTouched(true);
          }
          setReorderLevel(sample.reorderLevel);
          setMaxStockLevel(sample.maxStockLevel);
          // Absent on a property form, for the reason the SKU is.
          if (!isRealProperty) setManufacturer(sample.manufacturer);
          setBrandName(sample.brandName);
          setModel(sample.model);
          setExpiryThresholdDays(sample.expiryThresholdDays || '180');
          setUsefulLifeYears(sample.estimatedUsefulLifeYears);

          /*
            REQUIRED on a subscription, and the one field a generated digital asset could not
            reach by any other route — without it Save stays disabled on the first click, which
            is the Autogenerate contract broken with nothing failing.

            Empty on every other kind, because the server refuses a licensing model there.
          */
          setLicensingModel(sample.licensingModel);

          /*
            The DESCRIPTION is deliberately not filled. The editor is uncontrolled after mount
            — it owns the document — so setting the state behind it leaves the value and the
            screen disagreeing, which is worse than an empty field.
          */

          // The generator chose a category, which would otherwise re-fire the kind suggestion
          // and change the shape of the form under the reader. Setting a value is not the
          // reader choosing one, so the suggestion stops here exactly as it does on a keystroke.
          setKindTouched(true);
          setKindChoice(String(kind));
        },
        disabled: saving || loadingCatalogs,
      }}
      confirm={
        isLastStep
          ? {
              id: 'inventory-item-confirm',
              checked: confirmed,
              onChange: setConfirmed,
              disabled: saving,
            }
          : undefined
      }
      footer={
        <>
          <Button id="inventory-item-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          {/* Back pairs with Next and renders from the second step on. Never gated on validity:
              going back is how an invalid step gets fixed. */}
          {stepped && safeStepIndex > 0 ? (
            <Button
              id="inventory-item-back"
              variant="secondary"
              onClick={() => setStepIndex((s) => Math.max(0, s - 1))}
              disabled={saving}
            >
              Back
            </Button>
          ) : null}

{stepped && !isLastStep ? (
            <Tooltip disabledReason={missingOn(steps[safeStepIndex])}>
              <Button
                id="inventory-item-next"
                variant="secondary"
                onClick={() => setStepIndex((s) => Math.min(steps.length - 1, s + 1))}
                disabled={!STEP_VALID[steps[safeStepIndex]]}
              >
                Next
              </Button>
            </Tooltip>
          ) : null}

          <Tooltip disabledReason={saveBlockedReason()}>
            <Button
              id="inventory-item-submit"
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {stepped ? (
          <>
            {/*
              NO RAIL OVER ONE STEP. A digital asset's wizard has a single step, and a stepper
              with one head is a progress indicator for a journey of one — the same reason an
              EDIT is never stepped. The panel below is unchanged either way.
            */}
            {steps.length > 1 ? (
            <Stepper
              id="inventory-item"
              steps={steps.map((key) => ({ key, label: STEP_LABEL[key] }))}
              activeIndex={safeStepIndex}
              onJump={(index) => {
                // Forward movement requires every earlier step to be valid; back is free.
                if (index <= safeStepIndex || steps.slice(0, index).every((s) => STEP_VALID[s])) {
                  setStepIndex(index);
                }
              }}
            />
            ) : null}

            <div
              id={`inventory-item-panel-${currentStep}`}
              role="tabpanel"
              aria-labelledby={`inventory-item-step-${currentStep}`}
            >
              {CONTENT[currentStep]}
            </div>
          </>
        ) : (
          /* No legend: the modal TITLE already says which section this is, and a heading
             repeating it is the same value in two slots. */
          CONTENT[currentStep]
        )}
      </div>
    </Modal>
  );
}
